import { Inject, Injectable } from '@angular/core';

import { LocalStorageEnum } from '@enums/local-storage.enum';
import { UrlEnum } from '@enums/url.enum';
import { Store } from '@ngrx/store';
import { selectSessionData } from '@store/session/session.selectors';
import { AppState } from '@store/state';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalConstant } from 'src/app/core/constants/global.constant';
import { Urls } from 'src/app/core/constants/partials/url.constant';
import { ConfigService } from '../config/config.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private _parsedUrls: Urls = {} as Urls;

  constructor(
    @Inject(ConfigService) private _configService: ConfigService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(GlobalConstant) private _globalConstant: GlobalConstant
  ) {
    this.setParsedUrls();
  }

  public setParsedUrls(): void {
    const { urls } = this._globalConstant.url;
    Object.keys(urls).forEach((key) => {
      this._parsedUrls[key] = this.parseUrl(urls[key]);
    });
  }

  public parseUrl(url: string, params?: { [key: string]: string }): string {
    const OPTION_ORDER_API = this._configService.getProperty<string>('OPTION_ORDER_API_URL');
    const ORDER_API = this._configService.getProperty<string>('ORDER_API_URL');
    const WEB_API = this._configService.getProperty<string>('WEB_API_URL');
    const FAREFINDER_URL = this._configService.getProperty<string>('FAREFINDER_URL');
    const AUTHORIZATION_SERVER = this._configService.getProperty<string>('AUTH_SERVER');
    const CLIENT_ID = this._configService.getProperty<string>('CLIENT_ID');
    const FLIGHT_CHANGE_API = this._configService.getProperty<string>('FLIGHT_CHANGE_API_URL');
    const USER_SESSION_TOKEN = this._localStorageService.getUserSessionToken();
    const MYFF_BFF = this._configService.getProperty<string>('MYFF_BFF');

    if (params) {
      Object.keys(params).forEach((key) => {
        url = url.replace(`[${key}]`, params[key]);
      });
    }

    return url
      .replace('[OPTION_ORDER_API]', OPTION_ORDER_API)
      .replace('[ORDER_API]', ORDER_API)
      .replace('[WEB_API]', WEB_API)
      .replace('[FLIGHT_CHANGE_API]', FLIGHT_CHANGE_API)
      .replace('[FAREFINDER_URL]', FAREFINDER_URL)
      .replace('[AUTHORIZATION_SERVER]', AUTHORIZATION_SERVER)
      .replace('[CLIENT_ID]', CLIENT_ID)
      .replace(`[${LocalStorageEnum.OM_userSessionToken}]`, USER_SESSION_TOKEN)
      .replace(`[MYFF_BFF]`, MYFF_BFF);
  }

  public getFarefinderRedirectUrl(): Observable<string> {
    return this._store.select(selectSessionData).pipe(
      map((sessionData) => {
        const FAREFINDER_URL = this._configService.getProperty<string>('FAREFINDER_URL');
        return this.urls[UrlEnum.FAREFINDER_REDIRECT_URL].replace('[FAREFINDER_URL]', FAREFINDER_URL);
      })
    );
  }

  public parseDynamicUrl(name: string, { ...dynamicParams }: object): string {
    let url = this._parsedUrls[name];
    Object.entries(dynamicParams).forEach(([key, value]) => {
      url = url.replace(`[${key}]`, value);
    });
    return url;
  }

  public getLogoutUrl(): string {
    const authServerUrl: string = this._configService.getProperty<string>('AUTH_SERVER');
    return this.urls[UrlEnum.LOGOUT_URL]
      .replace('[AUTHORIZATION_SERVER]', authServerUrl)
      .replace('[window_origin]', window.location.origin);
  }

  get urls(): Urls {
    return this._parsedUrls;
  }
}
